/**
 * The office sky: how dark the office is and what colour its daylight has at a real instant and
 * place. Both renderers read it; only the 2.5D one turns it into shader uniforms.
 */

export type TownSkyPhase = "day" | "golden" | "dusk" | "night" | "dawn";

export type TownSky = Readonly<{
  /** 0 is full daylight, 1 full night. Lamps, computers and room lights add light in proportion. */
  night: number;
  /** Multiplies lit colour before the night tint: warm late in the afternoon, rose at dusk. */
  tint: readonly [number, number, number];
  phase: TownSkyPhase;
  /** Degrees above the horizon; negative after sunset. */
  sunElevation: number;
  /** Unit world vector toward the light that bands surfaces: the real sun, or the art's fixed light. */
  sunDirection: readonly [number, number, number];
}>;

/**
 * Hook for a future weather feed. Cloud cover greys the daylight and brings dusk forward a
 * little, so lamps come on earlier on an overcast evening.
 */
export type TownWeather = Readonly<{ cloudCover: number }>;

/** Linear multipliers for unlit surfaces at full night: moonlit outdoors, darker indoors. */
export const TOWN_NIGHT_OUTDOOR: readonly [number, number, number] = [0.16, 0.21, 0.4];
export const TOWN_NIGHT_INDOOR: readonly [number, number, number] = [0.13, 0.14, 0.24];
/** Indoors gets this share of the sky's colour by day; windows soften golden hour inside. */
export const TOWN_INDOOR_DAYLIGHT = 0.45;
/** The reference art's light: upper left, slightly from the north. North is -z. */
export const TOWN_ART_LIGHT_DIRECTION: readonly [number, number, number] = (() => {
  const length = Math.hypot(-0.4, 1, -0.1);
  return [-0.4 / length, 1 / length, -0.1 / length] as const;
})();

export const TOWN_DAY_SKY: TownSky = { night: 0, tint: [1, 1, 1], phase: "day", sunElevation: 60, sunDirection: TOWN_ART_LIGHT_DIRECTION };

const DEGREES = Math.PI / 180;

export type SunPosition = Readonly<{ elevation: number; azimuth: number }>;

/**
 * Sun elevation and azimuth in degrees (NOAA's low-precision series, within about half a degree).
 * Azimuth runs clockwise from north. The time zone does not matter: the instant is absolute and
 * longitude places the sun.
 */
export function sunPosition(instant: Date, latitude: number, longitude: number): SunPosition {
  const start = Date.UTC(instant.getUTCFullYear(), 0, 1);
  const dayOfYear = Math.floor((instant.getTime() - start) / 86_400_000);
  const hours = instant.getUTCHours() + instant.getUTCMinutes() / 60 + instant.getUTCSeconds() / 3600;
  const gamma = (2 * Math.PI / 365) * (dayOfYear + (hours - 12) / 24);
  const equationOfTime = 229.18 * (0.000075 + 0.001868 * Math.cos(gamma) - 0.032077 * Math.sin(gamma)
    - 0.014615 * Math.cos(2 * gamma) - 0.040849 * Math.sin(2 * gamma));
  const declination = 0.006918 - 0.399912 * Math.cos(gamma) + 0.070257 * Math.sin(gamma)
    - 0.006758 * Math.cos(2 * gamma) + 0.000907 * Math.sin(2 * gamma)
    - 0.002697 * Math.cos(3 * gamma) + 0.00148 * Math.sin(3 * gamma);
  const solarMinutes = hours * 60 + equationOfTime + 4 * longitude;
  const hourAngle = (solarMinutes / 4 - 180) * DEGREES;
  const lat = latitude * DEGREES;
  const cosZenith = Math.sin(lat) * Math.sin(declination) + Math.cos(lat) * Math.cos(declination) * Math.cos(hourAngle);
  const elevation = 90 - Math.acos(Math.min(1, Math.max(-1, cosZenith))) / DEGREES;
  // Measured from south, positive toward the west, then turned to run from north.
  const fromSouth = Math.atan2(Math.sin(hourAngle), Math.cos(hourAngle) * Math.sin(lat) - Math.tan(declination) * Math.cos(lat));
  const azimuth = ((fromSouth / DEGREES + 180) % 360 + 360) % 360;
  return { elevation, azimuth };
}

export function sunElevation(instant: Date, latitude: number, longitude: number): number {
  return sunPosition(instant, latitude, longitude).elevation;
}

/**
 * The world vector toward the sun for band shading. A low sun is lifted to 32° so tops stay in
 * the lit band and only side faces follow it; after sunset the light eases back to the art's fixed
 * direction.
 */
export function sunLightDirection(sun: SunPosition): readonly [number, number, number] {
  const elevation = Math.max(32, sun.elevation) * DEGREES;
  const azimuth = sun.azimuth * DEGREES;
  const real = [Math.sin(azimuth) * Math.cos(elevation), Math.sin(elevation), -Math.cos(azimuth) * Math.cos(elevation)] as const;
  const t = smooth(-6, 0, sun.elevation);
  const mixed = [0, 1, 2].map((axis) => TOWN_ART_LIGHT_DIRECTION[axis]! + (real[axis]! - TOWN_ART_LIGHT_DIRECTION[axis]!) * t);
  const length = Math.hypot(mixed[0]!, mixed[1]!, mixed[2]!) || 1;
  return [mixed[0]! / length, mixed[1]! / length, mixed[2]! / length];
}

/** True before solar noon, so the same elevation reads as dawn rather than dusk. */
function isMorning(instant: Date, longitude: number): boolean {
  const solarHours = (instant.getUTCHours() + instant.getUTCMinutes() / 60 + longitude / 15 + 48) % 24;
  return solarHours < 12;
}

const smooth = (edge0: number, edge1: number, value: number) => {
  const t = Math.min(1, Math.max(0, (value - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
};
type Rgb = readonly [number, number, number];
const mixRgb = (a: Rgb, b: Rgb, t: number): Rgb => [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];

const WHITE: Rgb = [1, 1, 1];
const GOLDEN: Rgb = [1, 0.78, 0.55];
const MORNING: Rgb = [1, 0.92, 0.86];
const DUSK: Rgb = [0.92, 0.7, 0.8];
const OVERCAST: Rgb = [0.8, 0.83, 0.88];

/**
 * Daylight at an elevation. Above 12° it is plain day; the light warms toward sunset, turns rose
 * through civil twilight and reaches full night 10° below the horizon. Every step is continuous,
 * so a running office drifts from one look to the next.
 */
export function skyForElevation(elevation: number, morning: boolean, weather?: TownWeather, sunDirection = TOWN_ART_LIGHT_DIRECTION): TownSky {
  const warm = 1 - smooth(2, 12, elevation);
  const dusk = 1 - smooth(-4, 2, elevation);
  const deep = 1 - smooth(-10, -4, elevation);
  let tint = mixRgb(WHITE, morning ? MORNING : GOLDEN, warm);
  tint = mixRgb(tint, DUSK, dusk * (1 - deep));
  tint = mixRgb(tint, WHITE, deep);
  let night = 0.55 * dusk + 0.45 * deep;
  const clouds = Math.min(1, Math.max(0, weather?.cloudCover ?? 0));
  if (clouds > 0) {
    tint = mixRgb(tint, mixRgb(tint, OVERCAST, 0.7), clouds);
    night = Math.max(night, Math.min(1, night + clouds * 0.25 * dusk + clouds * 0.12 * warm));
  }
  const phase: TownSkyPhase = deep >= 0.5 ? "night" : dusk > 0.05 ? (morning ? "dawn" : "dusk") : warm > 0.05 && !morning ? "golden" : "day";
  return { night, tint, phase, sunElevation: elevation, sunDirection };
}

type Place = Readonly<{ latitude: number; longitude: number }>;

/** The office sky: continuous colour and the sun's real direction. */
export function townSky(instant: Date, location: Place, weather?: TownWeather): TownSky {
  const sun = sunPosition(instant, location.latitude, location.longitude);
  return skyForElevation(sun.elevation, isMorning(instant, location.longitude), weather, sunLightDirection(sun));
}

/**
 * A fixed instant for previews and screenshots: `?officeTime=19:30` renders the office at that
 * local time today. Returns null when the URL does not ask for one.
 */
export function previewOfficeInstant(search: string, timeZone: string, now = new Date()): Date | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(new URLSearchParams(search).get("officeTime") ?? "");
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  const local = zonedClock(now, timeZone);
  const delta = ((hours - local.hours) * 60 + (minutes - local.minutes)) * 60_000 - local.seconds * 1000;
  return new Date(now.getTime() + delta);
}

/** Wall-clock hours, minutes and seconds at an instant in an IANA zone. */
export function zonedClock(instant: Date, timeZone: string): Readonly<{ hours: number; minutes: number; seconds: number }> {
  const parts = clockFormatter(timeZone).formatToParts(instant);
  const read = (type: Intl.DateTimeFormatPartTypes) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  return { hours: read("hour") % 24, minutes: read("minute"), seconds: read("second") };
}

const formatters = new Map<string, Intl.DateTimeFormat>();
function clockFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = formatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-GB", { timeZone, hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" });
    formatters.set(timeZone, formatter);
  }
  return formatter;
}
